import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import CabPage from "../CabPage";
import MobileNav from "../account/MobileNav";
import { Icon } from "../account/icons";
import { Dialog, useToast } from "../fleet/ui";
import {
  fetchMyRequests,
  fetchMyTrucks,
  fetchOffers,
  respondMatch,
  counterMatch,
  declineMatch,
  viewMatch,
  acceptOffer,
  rejectOffer,
  revealContact,
} from "../../utils/matchingApi";
import { LoadDetailsPanel, PartyCard, roleType } from "../ui/cards";
import { currencyId } from "../search/quick";
import { setUnreadOverride, refreshUnread } from "../useUnread";
import "../css/notifications.css";
import { codeSign, ORDER_TYPES, bodyTypeName, money as moneyId } from "../loads/common";
import { D } from "../search/icons";

const API = process.env.REACT_APP_API_URL;
const auth = () => ({ Authorization: `JWT ${window.localStorage.getItem("access")}` });

const TRUCK_STATUS = { ACTIVE: "Ищет груз", BUSY: "Занят", INACTIVE: "Неактивен" };
const SEEN_KEY = "cab_seen_offer_notices";
const DISMISSED_KEY = "cab_dismissed_notices";
const DEFAULT_PREFS = { notify_requests: true, notify_messages: true, notify_system: true };
const SYSTEM_STYLE = {
  order_accepted: { tile: "green", icon: "check" },
  order_status: { tile: "blue", icon: "box" },
  driver_location: { tile: "orange", icon: "pin" },
  broadcast: { tile: "purple", icon: "bell" },
  system: { tile: "gray", icon: "clock" },
};

const TABS = [
  { key: "all", label: "Все" },
  { key: "new", label: "Новые" },
  { key: "requests", label: "Запросы на мои машины" },
  { key: "offers", label: "Запросы на грузы" },
  { key: "messages", label: "Сообщения" },
  { key: "system", label: "Системные" },
];

const EMPTY_TEXT = {
  all: "Уведомлений пока нет",
  new: "Новых уведомлений нет",
  requests: "Запросов на ваши машины пока нет",
  offers: "Откликов на ваши грузы пока нет",
  messages: "Сообщения скоро появятся — раздел чатов ещё в разработке",
  system: "Системных уведомлений пока нет",
};

const money = (amount, currency) => {
  if (amount == null) return "по договоренности";
  return `${Number(amount).toLocaleString("ru-RU")} ${codeSign(currency)}`.trim();
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

const dayLabel = (iso) => {
  const d = new Date(iso);
  const diff = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);
  if (diff === 0) return "Сегодня";
  if (diff === 1) return "Вчера";
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });
};

const timeLabel = (iso) => {
  const d = new Date(iso);
  const t = d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  const diff = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);
  return diff === 1 ? `Вчера, ${t}` : t;
};

const fullTime = (iso) => {
  const d = new Date(iso);
  const t = d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  return `${dayLabel(iso)}, ${t}`;
};

const dateLabel = (v) => {
  if (!v) return "";
  const d = new Date(String(v).length === 10 ? `${v}T00:00:00` : v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });
};

const readSeen = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]"));
  } catch {
    return new Set();
  }
};
const readDismissed = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) || "[]"));
  } catch {
    return new Set();
  }
};
const writeDismissed = (set) => {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...set].slice(-500)));
  } catch {
    /* ignore */
  }
};
const writeSeen = (set) => {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify([...set].slice(-300)));
  } catch {
    /* ignore */
  }
};

const MATCH_STATE = {
  accepted: { title: "Запрос принят", tile: "green", icon: "check" },
  responded: { title: "Вы приняли запрос", tile: "green", icon: "check" },
  countered: { title: "Вы предложили цену", tile: "orange", icon: "card" },
  closed: { title: "Запрос закрыт", tile: "gray", icon: "clock" },
};

function matchToNotice(m, vehicles) {
  const decided = m.decided;
  const meta = decided ? MATCH_STATE[decided.state] || MATCH_STATE.closed : { title: "Новый запрос на вашу машину", tile: "blue", icon: "box" };
  const l = m.load || {};
  const v = vehicles[m.vehicle_id];
  return {
    id: `m:${m.match_id}`,
    kind: "requests",
    type: "match",
    ts: m.created_at,
    unread: !decided && !m.viewed,
    title: meta.title,
    sub: [`${l.from || "—"} → ${l.to || "—"}`, l.weight_t != null ? `${l.weight_t} т` : null, l.body_type?.name].filter(Boolean).join(", "),
    tile: meta.tile,
    icon: meta.icon,
    match: m,
    vehicle: v,
  };
}

function offerToNotice(o, order, seen) {
  const key = `o:${o.id}`;
  const name = o.carrier?.name || "Перевозчик";
  const counter = o.kind === "counter_offer";
  return {
    id: key,
    kind: "offers",
    type: "offer",
    ts: o.created_at,
    unread: !seen.has(key),
    title: counter ? "Встречное предложение по вашему грузу" : "Новый отклик на ваш груз",
    sub: `${name}: ${order?.from_address || o.load_id} → ${order?.to_address || "—"}`,
    tile: "purple",
    icon: "users",
    offer: o,
    order,
    driverName: name,
  };
}

function systemToNotice(n) {
  const st = SYSTEM_STYLE[n.notification_type] || SYSTEM_STYLE.system;
  return {
    id: `s:${n.id}`,
    kind: "system",
    type: "system",
    ts: n.created_at,
    unread: !n.is_read,
    title: n.title,
    sub: n.body,
    tile: st.tile,
    icon: st.icon,
    sys: n,
  };
}

const OFFER_STATUS = { OPEN: "Ожидает решения", ACCEPTED: "Принят", REJECTED: "Отклонён", EXPIRED: "Истёк", WITHDRAWN: "Отозван", CANCELLED: "Отменён" };

const PREF_OF = { match: "notify_requests", offer: "notify_requests", system: "notify_system" };

export default function NotificationsPage() {
  const user = useSelector((s) => s.auth.user);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // { kind, n }
  const [amount, setAmount] = useState("");
  const [contacts, setContacts] = useState({}); // offerId -> {loading, data, error}
  const [seenTick, setSeenTick] = useState(0);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [prefDraft, setPrefDraft] = useState(DEFAULT_PREFS);
  const [toast, toastNode] = useToast();

  const load = useCallback(async () => {
    const seen = readSeen();
    const out = [];
    const canFleet = ["COMPANY", "DRIVER", "DISPATCHER", "ADMIN"].includes(user?.role);
    const [feed, trucks, transports, orders, system] = await Promise.allSettled([
      canFleet ? fetchMyRequests() : Promise.resolve(null),
      canFleet ? fetchMyTrucks() : Promise.resolve(null),
      canFleet && user?.id ? axios.get(`${API}/transports/`, { params: { owner: user.id }, headers: auth() }) : Promise.resolve(null),
      user?.id && user.role !== "DRIVER" ? axios.post(`${API}/order_search/`, {}, { headers: auth() }) : Promise.resolve(null),
      axios.get(`${API}/auth/notifications/`, { headers: auth() }),
    ]).then((r) => r.map((p) => (p.status === "fulfilled" ? p.value : null)));
    // offers on my loads: the matching service is the source of truth (respond / counter offers)
    const myOrders = (orders?.data || []).filter((o) => o.owner === user?.id && o.status === 1).slice(0, 25);
    const offerLists = await Promise.allSettled(myOrders.map((o) => fetchOffers(o.uuid)));

    const vehicles = {};
    const raw = transports?.data;
    (Array.isArray(raw) ? raw : raw?.results || []).forEach((t) => {
      vehicles[t.id] = {
        name: [t.mark || t.model, t.government_number].filter(Boolean).join(" · ") || `Машина #${t.id}`,
        weight: t.max_load_weight,
        volume: t.max_load_volume,
      };
    });
    (trucks?.trucks || []).forEach((t) => {
      vehicles[t.vehicle_id] = { ...(vehicles[t.vehicle_id] || { name: `Машина #${t.vehicle_id}` }), status: t.status };
    });
    (feed?.matches || []).forEach((m) => out.push(matchToNotice(m, vehicles)));

    offerLists.forEach((r, i) => {
      if (r.status !== "fulfilled") return;
      (r.value?.offers || []).forEach((o) => out.push(offerToNotice(o, myOrders[i], seen)));
    });
    (Array.isArray(system?.data) ? system.data : []).forEach((n) => out.push(systemToNotice(n)));

    out.sort((a, b) => new Date(b.ts) - new Date(a.ts));
    setItems(out);
    setLoading(false);
  }, [user?.id, user?.role]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    axios
      .get(`${API}/auth/preferences/`, { headers: auth() })
      .then((r) => setPrefs({ ...DEFAULT_PREFS, ...r.data }))
      .catch(() => {});
  }, []);

  // wireframe: the topbar search filters the notification list as you type (no navigation on Enter)
  useEffect(() => {
    const input = document.getElementById("global-search");
    const onInput = () => setQ(input.value);
    const onSearch = (e) => e.preventDefault();
    input?.addEventListener("input", onInput);
    window.addEventListener("cab:global-search", onSearch);
    return () => {
      input?.removeEventListener("input", onInput);
      window.removeEventListener("cab:global-search", onSearch);
    };
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const seenNow = useMemo(() => readSeen(), [seenTick, items]);
  const withRead = useMemo(
    () =>
      items
        .filter((n) => !dismissed.has(n.id))
        .map((n) => {
          const unread = (n.type === "offer" ? !seenNow.has(n.id) : n.unread) && prefs[PREF_OF[n.type]] !== false;
          return { ...n, unread };
        }),
    [items, seenNow, dismissed, prefs],
  );

  const counts = useMemo(
    () => ({
      all: withRead.length,
      new: withRead.filter((n) => n.unread).length,
      requests: withRead.filter((n) => n.kind === "requests").length,
      offers: withRead.filter((n) => n.kind === "offers").length,
      messages: 0,
      system: withRead.filter((n) => n.kind === "system").length,
    }),
    [withRead],
  );

  // topbar bell mirrors the «Новые» counter of this page while it is open
  useEffect(() => {
    if (!loading) setUnreadOverride(counts.new);
  }, [loading, counts.new]);
  useEffect(
    () => () => {
      setUnreadOverride(null);
      refreshUnread();
    },
    [],
  );

  const visible = useMemo(() => {
    const needle = q.toLowerCase().trim();
    return withRead.filter((n) => {
      const byTab = tab === "all" ? true : tab === "new" ? n.unread : n.kind === tab;
      return byTab && (!needle || `${n.title} ${n.sub}`.toLowerCase().includes(needle));
    });
  }, [withRead, tab, q]);

  // sticky: a notice keeps its detail pane open even after it leaves the current tab/search
  const selected = withRead.find((n) => n.id === selectedId) || null;

  // < 768px the wireframe shows either the list or the detail (.show-detail)
  const [wide, setWide] = useState(() => window.matchMedia("(min-width: 768px)").matches);
  const [showDetail, setShowDetail] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const on = () => setWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  useEffect(() => {
    if (wide && !selected && visible[0]) setSelectedId(visible[0].id);
  }, [wide, selected, visible]);

  const select = (n) => {
    setSelectedId(n.id);
    setShowDetail(true);
  };

  const closeDialog = useCallback(() => setDialog(null), []);

  const act = async (fn, okText) => {
    setBusy(true);
    try {
      await fn();
      toast(okText);
      await load();
    } catch (e) {
      toast(e?.message || "Не удалось выполнить действие");
    } finally {
      setBusy(false);
    }
  };

  // «Отметить прочитанным»: real read state per source
  const markRead = async (n) => {
    try {
      if (n.type === "match") await viewMatch(n.match.match_id);
      else if (n.type === "offer") {
        const sn = readSeen();
        sn.add(n.id);
        writeSeen(sn);
        setSeenTick((x) => x + 1);
      } else await axios.post(`${API}/auth/notifications/${n.sys.id}/read/`, {}, { headers: auth() });
      await load();
      toast("Отмечено прочитанным");
    } catch {
      toast("Не удалось отметить прочитанным");
    }
  };

  const remove = async (n) => {
    try {
      if (n.type === "system") await axios.delete(`${API}/auth/notifications/${n.sys.id}/`, { headers: auth() });
      else {
        const d = new Set(dismissed);
        d.add(n.id);
        writeDismissed(d);
        setDismissed(d);
      }
      const next = visible.find((x) => x.id !== n.id);
      setSelectedId(next ? next.id : null);
      if (!next) setShowDetail(false);
      if (n.type === "system") await load();
    } catch {
      toast("Не удалось удалить уведомление");
    }
  };

  const openSettings = () => {
    setPrefDraft(prefs);
    setDialog({ kind: "settings" });
  };

  const groups = [];
  visible.forEach((n) => {
    const label = dayLabel(n.ts);
    const g = groups[groups.length - 1];
    if (g && g.label === label) g.items.push(n);
    else groups.push({ label, items: [n] });
  });

  const showContact = async (o) => {
    setContacts((c) => ({ ...c, [o.id]: { loading: true } }));
    try {
      const d = await revealContact(o.id);
      setContacts((c) => ({ ...c, [o.id]: { data: d } }));
    } catch (e) {
      const msg = e.status === 403 ? "Контакты доступны только автору груза." : e.message || "Не удалось получить контакты";
      setContacts((c) => ({ ...c, [o.id]: { error: msg } }));
    }
  };

  const renderDetail = (n) => {
    const tools = (withMore) => (
      <div className="detail-tools">
        <button aria-label="Назад" type="button" onClick={() => setShowDetail(false)}>
          ←
        </button>
        <span></span>
        <button aria-label="Удалить уведомление" type="button" onClick={() => setDialog({ kind: "delete", n })}>
          <Icon name="trash" />
        </button>
        {withMore && (
          <button aria-label="Еще" type="button" onClick={() => setDialog({ kind: "more", n })}>
            ···
          </button>
        )}
      </div>
    );
    const title = (
      <header className="detail-title">
        <span className={`tile ${n.tile}`}>
          <Icon name={n.icon} />
        </span>
        <div>
          <h2>{n.title}</h2>
          <p>{fullTime(n.ts)}</p>
        </div>
        {n.unread && <i className="unread"></i>}
      </header>
    );

    if (n.type === "system") {
      return (
        <article className="detail" id="detail">
          {tools(false)}
          {title}
          <div className="generic-detail">{n.sub}</div>
          <button className="button" type="button" onClick={() => markRead(n)}>
            Отметить прочитанным
          </button>
        </article>
      );
    }

    if (n.type === "offer") {
      const o = n.offer;
      const t = o.truck || {};
      const c = o.carrier || {};
      const od = n.order || {};
      const open = o.status === "OPEN";
      const rc = contacts[o.id];
      const cargoLine = [ORDER_TYPES[od.item?.type], od.item?.weight != null ? `${od.item.weight} т` : null, od.item?.volume != null ? `${od.item.volume} м³` : null, bodyTypeName(od.transport_body_type)]
        .filter((x) => x && x !== "—")
        .join(" · ");
      return (
        <article className="detail" id="detail">
          {tools(true)}
          {title}
          <p className="intro-text">
            <strong>{c.company || n.driverName}</strong> {o.kind === "counter_offer" ? "предлагает свою цену" : "откликнулся"} на ваш груз по маршруту{" "}
            <strong>
              {od.from_address || "—"} → {od.to_address || "—"}
            </strong>
            {o.amount != null && (
              <>
                {" "}
                — ставка <strong>{money(o.amount, o.currency)}</strong>
              </>
            )}
            .
          </p>
          <section className="cargo-card">
            <h3>Груз</h3>
            <dl>
              <dt>Маршрут</dt>
              <dd>
                {od.from_address || "—"} → {od.to_address || "—"}
              </dd>
              <dt>Погрузка</dt>
              <dd>{od.from_date ? `${dateLabel(od.from_date)}${od.to_date && od.to_date !== od.from_date ? ` — ${dateLabel(od.to_date)}` : ""}` : "—"}</dd>
              <dt>Груз</dt>
              <dd>{cargoLine || "—"}</dd>
              <dt>Ваша ставка</dt>
              <dd>{od.price != null ? moneyId(od.price, od.currency) : "по договорённости"}</dd>
            </dl>
          </section>
          <section className="cargo-card">
            <h3>Предлагает транспорт</h3>
            <PartyCard
              name={c.name}
              company={c.company}
              type={roleType(c.role)}
              icon={c.company ? D.building : D.people}
              phone={rc?.data?.carrier?.phone || rc?.data?.phone || rc?.data?.phone_number}
              sub={[t.mark, t.government_number].filter(Boolean).join(" ")}
            />
            <dl>
              <dt>Машина</dt>
              <dd>{[t.mark, t.model && t.model !== t.mark ? t.model : null, t.government_number].filter(Boolean).join(" · ") || "—"}</dd>
              <dt>Кузов / тоннаж</dt>
              <dd>{[t.body_type, t.max_load_weight != null ? `${t.max_load_weight} т` : null].filter(Boolean).join(" · ") || "—"}</dd>
              <dt>Где сейчас</dt>
              <dd>{[t.city, o.distance_km != null ? `до погрузки ${Math.round(o.distance_km)} км` : null].filter(Boolean).join(" · ") || "—"}</dd>
              <dt>Статус отклика</dt>
              <dd>{OFFER_STATUS[o.status] || o.status}</dd>
            </dl>
            {rc?.error && <p role="alert" style={{ color: "#c0392b" }}>{rc.error}</p>}
          </section>
          <footer className="request-actions">
            {open ? (
              <>
                <button className="button primary" style={{ color: "#fff" }} type="button" disabled={busy} onClick={() => setDialog({ kind: "offerAccept", n })}>
                  Принять
                </button>
                <button className="button" type="button" disabled={busy} onClick={() => setDialog({ kind: "offerReject", n })}>
                  Отказать
                </button>
              </>
            ) : (
              <button className="button primary" type="button" disabled>
                {OFFER_STATUS[o.status] || o.status}
              </button>
            )}
            <button className="button" type="button" onClick={() => setDialog({ kind: "load", n })}>
              Детали груза
            </button>
            {(open || o.status === "ACCEPTED") && !rc?.data ? (
              <button className="button" type="button" disabled={rc?.loading} onClick={() => showContact(o)}>
                {rc?.loading ? "Загрузка…" : "Показать контакты"}
              </button>
            ) : null}
            <button className="button" type="button" onClick={() => setDialog({ kind: "more", n })}>
              ···
            </button>
          </footer>
        </article>
      );
    }

    const m = n.match;
    const l = m.load || {};
    const v = n.vehicle;
    const dec = m.decided;
    const decidedLabel = dec ? (dec.state === "accepted" || dec.state === "responded" ? "Запрос принят" : dec.state === "countered" ? "Цена предложена" : "Запрос закрыт") : null;
    return (
      <article className="detail" id="detail">
        {tools(true)}
        {title}
        <p className="intro-text">
          {l.customer ? (
            <>
              Компания <strong>{l.customer}</strong> отправила
            </>
          ) : (
            "Отправлен"
          )}{" "}
          запрос на вашу машину <strong>{v?.name || `#${m.vehicle_id}`}</strong> на перевозку груза по маршруту{" "}
          <strong>
            {l.from || "—"} → {l.to || "—"}.
          </strong>
        </p>
        <button className="vehicle-card" type="button" onClick={() => setDialog({ kind: "vehicle", n })}>
          <span className="tile blue" style={{ width: 70, height: 70 }}>
            <Icon name="truck" />
          </span>
          <span>
            <strong>{v?.name || `Машина #${m.vehicle_id}`}</strong>
            <small>
              {l.body_type?.name && (
                <>
                  <Icon name="truck" /> {l.body_type.name}{" "}
                </>
              )}
              {v?.weight != null && (
                <>
                  <Icon name="box" /> {v.weight} т{" "}
                </>
              )}
              {v?.volume != null && (
                <>
                  <Icon name="box" /> {v.volume} м³
                </>
              )}
            </small>
          </span>
          {v?.status && <b className="availability">◉ {TRUCK_STATUS[v.status] || v.status}</b>}
          <b className="next">›</b>
        </button>
        <section className="cargo-card">
          <h3>Информация о грузе</h3>
          <div className="route">
            <span className="pin blue">
              <Icon name="pin" />
            </span>
            <div>
              <strong>{[l.from, l.from_country].filter(Boolean).join(", ") || "—"}</strong>
              <small>{l.loading_from ? `${dateLabel(l.loading_from)} (загрузка)` : "Дата загрузки не указана"}</small>
            </div>
            <span className="route-line">⟶</span>
            <span className="pin red">
              <Icon name="pin" />
            </span>
            <div>
              <strong>{[l.to, l.to_country].filter(Boolean).join(", ") || "—"}</strong>
              <small>{l.loading_to ? `до ${dateLabel(l.loading_to)}` : " "}</small>
            </div>
          </div>
          <div className="cargo-stats">
            <div>
              <Icon name="truck" />
              <span>
                <small>Требуемый кузов</small>
                <p>{l.body_type?.name || "—"}</p>
              </span>
            </div>
            <div>
              <Icon name="box" />
              <span>
                <small>Вес / Объем</small>
                <b>{l.weight_t != null ? `${Number(l.weight_t).toLocaleString("ru-RU")} т` : "—"}</b>
                <small>{l.volume_m3 != null ? `${l.volume_m3} м³` : " "}</small>
              </span>
            </div>
            <div>
              <Icon name="card" />
              <span>
                <small>Ставка</small>
                <b>{money(l.price, l.currency)}</b>
                <small>{dec?.state === "countered" ? `Ваша цена: ${money(dec.amount, dec.currency)}` : "Предложение"}</small>
              </span>
            </div>
          </div>
          <h3>Дополнительная информация</h3>
          <dl>
            <dt>Совпадение</dt>
            <dd>{m.score != null ? `${Math.round(m.score)}%` : "—"}</dd>
            {m.distance_km != null && (
              <>
                <dt>Расстояние до погрузки</dt>
                <dd>{Math.round(m.distance_km)} км</dd>
              </>
            )}
            {dec && (
              <>
                <dt>Ваш ответ</dt>
                <dd>{(MATCH_STATE[dec.state] || MATCH_STATE.closed).title}</dd>
              </>
            )}
          </dl>
        </section>
        <footer className="request-actions">
          {dec ? (
            <button className="button primary" type="button" disabled>
              {decidedLabel}
            </button>
          ) : (
            <>
              <button className="button primary" type="button" disabled={busy} onClick={() => setDialog({ kind: "accept", n })}>
                Принять запрос
              </button>
              <button
                className="button outline"
                type="button"
                disabled={busy}
                onClick={() => {
                  setAmount(l.price != null ? String(l.price) : "");
                  setDialog({ kind: "price", n });
                }}
              >
                Предложить свою цену
              </button>
            </>
          )}
          {!dec && (
            <button className="button danger" type="button" disabled={busy} onClick={() => setDialog({ kind: "decline", n })}>
              Отклонить
            </button>
          )}
          <button className="button" type="button" onClick={() => setDialog({ kind: "load", n })}>
            Детали груза
          </button>
          <button className="button" type="button" onClick={() => setDialog({ kind: "more", n })}>
            ···
          </button>
        </footer>
      </article>
    );
  };

  const d = dialog;
  const dl = d?.n?.match?.load || {};
  const routeText = (l) => `${l.from || "—"} → ${l.to || "—"}`;

  return (
    <CabPage name="notifications">
      <main id="main">
        <div className="page-heading">
          <div>
            <h1>
              Уведомления {counts.new > 0 && <b className="red-count">{counts.new}</b>}
            </h1>
            <p>Все важные события в одном месте</p>
          </div>
          <button className="button settings-button" type="button" onClick={openSettings}>
            <Icon name="gear" />
            Настройки уведомлений
          </button>
        </div>
        <div className="tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              type="button"
              aria-selected={tab === t.key}
              className={tab === t.key ? "active" : ""}
              onClick={() => {
                setTab(t.key);
                setSelectedId(null);
                setShowDetail(false);
              }}
            >
              {t.label} <span>{counts[t.key]}</span>
            </button>
          ))}
        </div>
        <div className={`notification-layout${showDetail ? " show-detail" : ""}`}>
          <section aria-label="Уведомления" className="notification-list">
            {groups.map((g) => (
              <React.Fragment key={g.label}>
                <h3 className="day">{g.label}</h3>
                {g.items.map((n) => (
                  <button key={n.id} type="button" className={`notice${selected?.id === n.id ? " selected" : ""}`} onClick={() => select(n)}>
                    <span className={`tile ${n.tile}`}>
                      <Icon name={n.icon} />
                    </span>
                    <span className="notice-text">
                      <strong>{n.title}</strong>
                      <small>{n.sub}</small>
                    </span>
                    <time>{timeLabel(n.ts)}</time>
                    {n.unread && <i className="unread"></i>}
                  </button>
                ))}
              </React.Fragment>
            ))}
            {!loading && visible.length === 0 && (
              <p id="empty" style={{ padding: "28px 11px", color: "#5e77a1" }}>
                {q.trim() ? "Уведомлений нет" : EMPTY_TEXT[tab]}
              </p>
            )}
            {loading && <p style={{ padding: "28px 11px", color: "#5e77a1" }}>Загрузка…</p>}
          </section>
          {selected ? renderDetail(selected) : <article className="detail" id="detail"><p className="empty-note">Выберите уведомление</p></article>}
        </div>
      </main>

      {d?.kind === "accept" && (
        <Dialog title="Принять запрос" onClose={closeDialog}>
          <p>
            {[dl.customer, routeText(dl), money(dl.price, dl.currency)].filter(Boolean).join(" · ")}
          </p>
          <button
            className="button primary"
            type="button"
            onClick={() => {
              closeDialog();
              act(() => respondMatch(d.n.match.match_id), "Запрос принят — ожидайте ответа заказчика");
            }}
          >
            Подтвердить
          </button>
        </Dialog>
      )}

      {d?.kind === "price" && (
        <Dialog title="Предложить свою цену" onClose={closeDialog}>
          <form
            id="price-form"
            onSubmit={(e) => {
              e.preventDefault();
              const value = Number(amount);
              if (!value || value <= 0) return;
              closeDialog();
              act(() => counterMatch(d.n.match.match_id, value), `Предложение ${value.toLocaleString("ru-RU")} ${codeSign(dl.currency)} отправлено заказчику`.replace("  ", " "));
            }}
          >
            <label>
              Ставка{dl.currency ? `, ${codeSign(dl.currency)}` : ""}
              <input name="price" type="number" min="1" required value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
            </label>
            <button className="button primary" type="submit">
              Отправить предложение
            </button>
          </form>
        </Dialog>
      )}

      {d?.kind === "vehicle" && (
        <Dialog title={d.n.vehicle?.name || `Машина #${d.n.match.vehicle_id}`} onClose={closeDialog}>
          <p>
            {[dl.body_type?.name, d.n.vehicle?.weight != null ? `${d.n.vehicle.weight} т` : null, d.n.vehicle?.volume != null ? `${d.n.vehicle.volume} м³` : null]
              .filter(Boolean)
              .join(" · ") || "Характеристики не указаны"}
          </p>
          {d.n.vehicle?.status && <p>Статус: {(TRUCK_STATUS[d.n.vehicle.status] || d.n.vehicle.status).toLowerCase()}.</p>}
        </Dialog>
      )}

      {d?.kind === "more" && (
        <Dialog title="Действия с уведомлением" onClose={closeDialog}>
          <button
            className="button"
            type="button"
            onClick={() => {
              closeDialog();
              markRead(d.n);
            }}
          >
            Отметить прочитанным
          </button>{" "}
          <button className="button" type="button" onClick={() => setDialog({ kind: "delete", n: d.n })}>
            Удалить
          </button>
        </Dialog>
      )}

      {d?.kind === "decline" && (
        <Dialog title="Отклонить запрос?" onClose={closeDialog}>
          <p>{[dl.customer, routeText(dl), money(dl.price, dl.currency)].filter(Boolean).join(" · ")}</p>
          <button
            className="button primary"
            type="button"
            onClick={() => {
              closeDialog();
              act(() => declineMatch(d.n.match.match_id), "Запрос отклонён");
            }}
          >
            Отклонить запрос
          </button>
        </Dialog>
      )}

      {d?.kind === "offerAccept" && (
        <Dialog title="Принять предложение?" onClose={closeDialog}>
          <p>
            <strong>{d.n.offer.carrier?.company || d.n.driverName}</strong>
            <br />
            {d.n.order?.from_address} → {d.n.order?.to_address}
            {d.n.offer.amount != null ? ` · ${money(d.n.offer.amount, d.n.offer.currency)}` : ""}
          </p>
          <p>После принятия груз будет закрыт для других перевозчиков и переведён в статус «В работе».</p>
          <button
            className="button primary"
            type="button"
            onClick={() => {
              closeDialog();
              act(() => acceptOffer(d.n.offer.load_id, d.n.offer.id), "Перевозчик выбран — груз переведён в «В работе»");
            }}
          >
            Принять
          </button>
        </Dialog>
      )}

      {d?.kind === "offerReject" && (
        <Dialog title="Отказать перевозчику?" onClose={closeDialog}>
          <p>
            <strong>{d.n.offer.carrier?.company || d.n.driverName}</strong> получит уведомление, груз останется доступным для других перевозчиков.
          </p>
          <button
            className="button primary"
            type="button"
            onClick={() => {
              closeDialog();
              act(() => rejectOffer(d.n.offer.id), "Предложение отклонено");
            }}
          >
            Отказать
          </button>
        </Dialog>
      )}

      {d?.kind === "load" && <NoticeLoad n={d.n} onClose={closeDialog} />}

      {d?.kind === "delete" && (
        <Dialog title="Удалить уведомление?" onClose={closeDialog}>
          <p>{d.n.type === "system" ? "Уведомление будет удалено." : "Уведомление будет скрыто из списка."}</p>
          <button
            className="button primary"
            type="button"
            onClick={() => {
              closeDialog();
              remove(d.n);
            }}
          >
            Удалить
          </button>
        </Dialog>
      )}

      {d?.kind === "settings" && (
        <Dialog title="Настройки уведомлений" onClose={closeDialog}>
          <form
            className="settings-form"
            id="preferences"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                const r = await axios.patch(`${API}/auth/preferences/`, prefDraft, { headers: auth() });
                setPrefs({ ...DEFAULT_PREFS, ...r.data });
                closeDialog();
                toast("Настройки сохранены");
              } catch {
                toast("Не удалось сохранить настройки");
              }
            }}
          >
            {[
              ["notify_requests", "Запросы на мои машины"],
              ["notify_messages", "Сообщения"],
              ["notify_system", "Системные события"],
            ].map(([k, label]) => (
              <label key={k}>
                <input type="checkbox" name={k} checked={prefDraft[k] !== false} onChange={(e) => setPrefDraft((s) => ({ ...s, [k]: e.target.checked }))} /> {label}
              </label>
            ))}
            <button className="button primary" type="submit">
              Сохранить
            </button>
          </form>
          <p className="demo-note">Отключённые типы не помечаются как новые и не попадают в счётчик у колокольчика.</p>
        </Dialog>
      )}

      {toastNode}
      <MobileNav />
    </CabPage>
  );
}

/** «Детали груза» for a notification: the full order from Django, or what the notification itself knows. */
function NoticeLoad({ n, onClose }) {
  const l = n.match?.load;
  const id = n.type === "match" ? l?.id : n.offer?.load_id;
  const [order, setOrder] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!id) {
      setFailed(true);
      return undefined;
    }
    let alive = true;
    axios
      .get(`${API}/orders/${id}/`, { headers: auth() })
      .then((r) => alive && setOrder(r.data))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [id]);
  if (order) return <LoadDetailsPanel order={order} onClose={onClose} />;
  if (failed && l) {
    const fallback = {
      uuid: l.id,
      from_address: l.from,
      to_address: l.to,
      from_date: l.loading_from,
      to_date: l.loading_to,
      price: l.price,
      currency: currencyId(l.currency),
      item: { weight: l.weight_t, volume: l.volume_m3 },
      contacts: { company_name: l.customer },
    };
    return <LoadDetailsPanel order={fallback} onClose={onClose} />;
  }
  return (
    <LoadDetailsPanel order={{ uuid: id, from_address: failed ? "Груз недоступен" : "Загрузка…" }} title={failed ? "Груз недоступен" : "Загрузка…"} onClose={onClose} />
  );
}

